// Finance module route definitions.
// Mounted only when the caller's org has the finance module enabled — App.tsx
// filters via clientModuleRegistry.filter(enabledIds).flatMap((m) => m.routes).

import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '../../../client/src/components/auth/ProtectedRoute.js';
import { SkeletonList } from '../../../client/src/components/ui/Skeleton.js';
import { LEGACY_TESTER_PATH, TESTER_PATH } from './pages/tester/tester-path.js';

// Lazy page factories — match the React.lazy calls in App.tsx exactly.
const TradingDashboardPage = lazy(() =>
  import('./pages/TradingDashboard.js').then((m) => ({ default: m.TradingDashboardPage })),
);
const TradingPage = lazy(() => import('./pages/Trading.js').then((m) => ({ default: m.TradingPage })));
const StrategiesPage = lazy(() => import('./pages/Strategies.js').then((m) => ({ default: m.StrategiesPage })));
const StrategyDetailPage = lazy(() =>
  import('./pages/StrategyDetailPage.js').then((m) => ({ default: m.StrategyDetailPage })),
);
const TesterPage = lazy(() => import('./pages/tester/TesterPage.js').then((m) => ({ default: m.TesterPage })));
const PortfolioPage = lazy(() => import('./pages/Portfolio.js').then((m) => ({ default: m.PortfolioPage })));
const WatchlistPage = lazy(() => import('./pages/Watchlist.js').then((m) => ({ default: m.WatchlistPage })));
const WatchlistAddPage = lazy(() => import('./pages/WatchlistAdd.js').then((m) => ({ default: m.WatchlistAddPage })));
const WalletAddPage = lazy(() => import('./pages/WalletAdd.js').then((m) => ({ default: m.WalletAddPage })));
const WalletDetailPage = lazy(() => import('./pages/WalletDetail.js').then((m) => ({ default: m.WalletDetailPage })));
const CompanyDetailPage = lazy(() =>
  import('./pages/CompanyDetail.js').then((m) => ({ default: m.CompanyDetailPage })),
);
const PortfoliosPage = lazy(() => import('./pages/Portfolios.js').then((m) => ({ default: m.PortfoliosPage })));
const PortfolioNewPage = lazy(() => import('./pages/PortfolioNew.js').then((m) => ({ default: m.PortfolioNewPage })));
const PortfolioDetailPage = lazy(() =>
  import('./pages/PortfolioDetail.js').then((m) => ({ default: m.PortfolioDetailPage })),
);
const MLInsightsPage = lazy(() => import('./pages/MLInsights.js').then((m) => ({ default: m.MLInsightsPage })));
const RiskManagementPage = lazy(() =>
  import('./pages/RiskManagement.js').then((m) => ({ default: m.RiskManagementPage })),
);
const IndicatorsPage = lazy(() => import('./pages/Indicators.js').then((m) => ({ default: m.IndicatorsPage })));
const MarketDataIngestionPage = lazy(() =>
  import('./pages/MarketDataIngestion.js').then((m) => ({ default: m.MarketDataIngestionPage })),
);

const FinanceLoading = () => (
  <div className="mx-auto max-w-3xl py-12">
    <SkeletonList label="Loading page" count={4} />
  </div>
);

/** Query-preserving redirect: a bookmarked `/finance/tester?def=&run=` opens the same strategy and run. */
const RedirectTester = () => {
  const { search } = useLocation();
  return <Navigate to={`${TESTER_PATH}${search}`} replace />;
};

export const financeRoutes: RouteObject[] = [
  {
    path: '/finance',
    element: (
      <ProtectedRoute policy="CanViewPortfolio">
        <Suspense fallback={<FinanceLoading />}>
          <TradingDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/trading',
    element: (
      <ProtectedRoute policy="CanTrade">
        <Suspense fallback={<FinanceLoading />}>
          <TradingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/strategies',
    element: (
      <ProtectedRoute policy="CanViewStrategyResults">
        <Suspense fallback={<FinanceLoading />}>
          <StrategiesPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Strategy Tester (A3 U3), opened from the strategy list: `?def=&run=` select the strategy and its run. A static
    // segment, so it wins over `:defId` below.
    path: TESTER_PATH,
    element: (
      <ProtectedRoute policy="CanViewStrategyResults">
        <Suspense fallback={<FinanceLoading />}>
          <TesterPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  { path: LEGACY_TESTER_PATH, element: <RedirectTester /> },
  {
    // Deep-linkable definition workbench. `?run=<id>` selects a run on the Runs tab.
    path: '/finance/strategies/:defId',
    element: (
      <ProtectedRoute policy="CanManageStrategies">
        <Suspense fallback={<FinanceLoading />}>
          <StrategyDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/portfolio',
    element: (
      <ProtectedRoute policy="CanViewPortfolio">
        <Suspense fallback={<FinanceLoading />}>
          <PortfolioPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/watchlist',
    element: (
      <ProtectedRoute policy="CanViewResearch">
        <Suspense fallback={<FinanceLoading />}>
          <WatchlistPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/watchlist/new',
    element: (
      <ProtectedRoute policy="CanManageWatchlists">
        <Suspense fallback={<FinanceLoading />}>
          <WatchlistAddPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Wallet tracking W6: two segments, so they never reach the one-segment `:ticker` route below.
  {
    path: '/finance/watchlist/wallets/new',
    element: (
      <ProtectedRoute policy="CanManageWatchlists">
        <Suspense fallback={<FinanceLoading />}>
          <WalletAddPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/watchlist/wallets/:id',
    element: (
      <ProtectedRoute policy="CanViewResearch">
        <Suspense fallback={<FinanceLoading />}>
          <WalletDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/watchlist/:ticker',
    element: (
      <ProtectedRoute policy="CanViewResearch">
        <Suspense fallback={<FinanceLoading />}>
          <CompanyDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/portfolios',
    element: (
      <ProtectedRoute policy="CanViewPortfolio">
        <Suspense fallback={<FinanceLoading />}>
          <PortfoliosPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/portfolios/new',
    element: (
      <ProtectedRoute policy="CanTrade">
        <Suspense fallback={<FinanceLoading />}>
          <PortfolioNewPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/portfolios/:id',
    element: (
      <ProtectedRoute policy="CanViewPortfolio">
        <Suspense fallback={<FinanceLoading />}>
          <PortfolioDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/ml-insights',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <Suspense fallback={<FinanceLoading />}>
          <MLInsightsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/risk',
    element: (
      <ProtectedRoute policy="CanManageRisk">
        <Suspense fallback={<FinanceLoading />}>
          <RiskManagementPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/finance/indicators',
    element: (
      <ProtectedRoute policy="CanViewResearch">
        <Suspense fallback={<FinanceLoading />}>
          <IndicatorsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Operator surface: list the org's market-data ingestion jobs and run one
    // now. Power-admin only — the server gates both calls on the same policy.
    path: '/finance/market-data/ingestion',
    element: (
      <ProtectedRoute policy="CanRunIngestionJobs">
        <Suspense fallback={<FinanceLoading />}>
          <MarketDataIngestionPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
];
